import { useState, useEffect, MouseEvent } from "react";
import { TableHead } from "@kaltura/mediaspace-shared-styled";
import { TableBody, TableRow, Stack } from "@kaltura/mediaspace-shared-styled";
import { Table, TableCell, IconButton } from "@kaltura/ds-react-components";
import { translate, translateTime, baseUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";
import { GuestAccessLink } from "../GuestAccessLink";
import { Copy24Icon, Delete24Icon } from "@kaltura/ds-react-icons";
import { CopyButton, ConfirmationPopover } from "@kaltura/mediaspace-shared-ui";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface LinksTableProps {
    entryId: string;
    links: GuestAccessLink[];
    onDelete: (ks: string) => void;
}

/**
 * Guest Access links table
 */
export const LinksTable = (props: LinksTableProps) => {
    const { entryId, links, onDelete } = props;
    const [processing, setProcessing] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [ks, setKs] = useState<string | null>(null);
    const open = Boolean(anchorEl);

    const context = useConfig();
    const { dateFormats } = context.application;
    const sendButtonAnalytics = useButtonAnalytics();

    useEffect(() => {
        setProcessing(null);
        setAnchorEl(null);
        setKs(null);
    }, [links]);

    const handleDelete = (event: MouseEvent<HTMLElement>, ks: string) => {
        setAnchorEl(event.currentTarget);
        setKs(ks);
    };

    const handleConfirmDelete = () => {
        ks && setProcessing(ks);
        ks && onDelete(ks);
    };

    const handleCancelDelete = () => {
        setAnchorEl(null);
        setKs(null);
    };

    const handleCopyTextClick = () => {
        sendButtonAnalytics("Guest access link - Copy", ButtonClickAnalyticsType.SHARE);
    };

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{translate("Link name")}</TableCell>
                        <TableCell>{translate("Expiration date")}</TableCell>
                        <TableCell>{translate("# of clicks")}</TableCell>
                        <TableCell>{translate("Actions")}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {links.map((link) => {
                        return (
                            <TableRow key={link.ks}>
                                <TableCell>{link.name}</TableCell>
                                <TableCell>{translateTime(link.expiration, dateFormats.shortDate)}</TableCell>
                                <TableCell>{link.clicks > 0 ? link.clicks : translate("unlimited")}</TableCell>
                                <TableCell>
                                    <Stack direction={"row"} gap={2}>
                                        <CopyButton
                                            toolTipLabel={translate("link copied to clipboard")}
                                            text={`${serverUrl}/${baseUrl}invitetomedia/guest-links/lobby/id/${entryId}?ks=${link.ks}`}
                                            onClick={handleCopyTextClick}
                                        >
                                            <IconButton
                                                aria-label={translate("copy link %1 to clipboard", [link.name])}
                                                color="secondary"
                                                disabled={processing === link.ks}
                                            >
                                                <Copy24Icon />
                                            </IconButton>
                                        </CopyButton>
                                        <IconButton
                                            aria-label={translate("delete link %1", [link.name])}
                                            color="secondary"
                                            onClick={(event) => handleDelete(event, link.ks)}
                                            loading={processing === link.ks}
                                            disabled={!!processing}
                                        >
                                            <Delete24Icon />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <ConfirmationPopover
                message={translate("Are you sure you want to delete this link?")}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                confirmLabel={translate("Yes, Delete")}
                cancelLabel={translate("Close")}
            />
        </>
    );
};
